import { Address } from '@/modules/address/models/Address';
import { AuthUserPermission } from '@/modules/auth/models/AuthUserPermission';
import { UnservedReason } from '@/modules/beneficiary/enums/UnservedReason';
import { Billing } from '@/modules/billing/models/Billing';
import { CalendarMeetingLinkType } from '@/modules/calendarmeetinglink/enums/CalendarMeetingLinkType';
import { CalendarMeetingLink } from '@/modules/calendarmeetinglink/models/CalendarMeetingLink';
import { Company } from '@/modules/company/models/Company';
import { Contact } from '@/modules/contact/models/Contact';
import { numberOfDaysFromTodayToDate } from '@/modules/core/app/helpers/DateHelper';
import { EstateRelationshipType } from '@/modules/estate/enums/EstateRelationshipType';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { DistributionAlternate } from '@/modules/estatedistribution/models/DistributionAlternate';
import { EstateNomination } from '@/modules/estatenomination/models/EstateNomination';
import { EstateSignUpToken } from '@/modules/EstateSignUpToken/models/EstateSignUpToken';
import { UserEstateTaskCollection } from '@/modules/estatetask/models/UserEstateTaskCollection';
import { Metric } from '@/modules/metric/models/Metric';
import { MailNotification } from '@/modules/notification/models/MailNotification';
import { OtpContactCollection } from '@/modules/otp/models/OtpContactCollection';
import { PartnershipInvitation } from '@/modules/partnershipinvitation/models/PartnershipInvitation';
import { Professional } from '@/modules/professional/models/Professional';
import { ProtectionMandataryVerifier } from '@/modules/protectionmandate/models/ProtectionMandataryVerifier';
import { Referral } from '@/modules/referral/models/Referral';
import { PredefinedEstateRole } from '@/modules/role/enums/PredefinedEstateRole';
import { TeamType } from '@/modules/team/enums/TeamType';
import { Team } from '@/modules/team/models/Team';
import { TeamInvite } from '@/modules/team/models/TeamInvite';
import { TeamUser } from '@/modules/team/models/TeamUser';
import { TimeEntryCollection } from '@/modules/timeentry/models/TimeEntryCollection';
import { TrustPilot } from '@/modules/trustpilot/models/TrustPilot';
import { BeneficiaryCategory } from '/@/modules/beneficiary/enums/BeneficiaryCategory';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import Lang from '/@/modules/core/app/services/Lang';
import { EsignEnvelopeType } from '/@/modules/esign/constants/EsignEnvelopeType';
import { PredefinedUserRole } from '/@/modules/role/enums/PredefinedUserRole';
import { HubSpotMeetingLinkType } from '/@/modules/user/constants/HubSpotMeetingLinkType';
import Locale from '/@/modules/user/Enum/Locale';
import { HubspotMeetingLink } from '/@/modules/user/models/HubspotMeetingLink';
import { UserAvailability } from '/@/modules/user/models/UserAvailability';
import { Note } from '@/modules/note/models/Note';
import { OutlookAccount } from '@/modules/auth/models/OutlookAccount';

export class User extends BaseEntityModel {
  id?: number;
  i?: number;
  email?: string;
  password?: string;
  emailVerifiedAt?: string;
  passwordChangedAt?: string;
  isSignedUp?: boolean;
  roleId?: number;
  active?: boolean;
  isDemo?: boolean;
  timezone?: string;
  lastLoginAt?: string;
  signedUpAt?: string;
  lastLoginIp?: string;
  avatarUrl?: string;
  homePage?: string;
  primaryContact?: string;
  invitationToken?: string | null;
  receiveUnreadInboxMessagesByMail?: boolean;
  hasDarkTheme?: boolean;
  stripePublishableKey?: string;
  locale?: string;
  hasPassword?: boolean;
  otpEnabled?: boolean;
  emailOtpEnabled?: boolean;
  contact: Relationship<Contact>;
  estateAccount?: Relationship<EstateAccount> | null;
  professional?: Relationship<Professional> | null;
  company?: Relationship<Company> | null;
  dependants?: Relationship<EstateAccount[]> | null;
  alternateBeneficiaryToEstateAccounts?: Relationship<EstateAccount[]> | null;
  alternateExecutorToEstateAccounts?: Relationship<EstateAccount[]> | null;
  mailNotifications?: Relationship<MailNotification[]> | null;
  hubspotMeetingLinks?: Relationship<HubspotMeetingLink[]> | null;
  metrics?: Relationship<Metric[]> | null;
  calendarMeetingLinks?: Relationship<CalendarMeetingLink[]> | null;
  timeEntries: Relationship<TimeEntryCollection>;
  trustPilot?: Relationship<TrustPilot> | null;
  teams?: Relationship<Team[]> | null;
  otpContacts: Relationship<OtpContactCollection>;
  estateTasks: Relationship<UserEstateTaskCollection>;
  billing?: Relationship<Billing> | null;
  partnershipInvitationId?: number;
  partnershipInvitation?: Relationship<PartnershipInvitation> | null;
  vaultSignUpToken?: Relationship<EstateSignUpToken> | null;
  settlementSignUpToken?: Relationship<EstateSignUpToken> | null;
  teamUsers?: Relationship<TeamUser[]> | null;
  teamInvites?: Relationship<TeamInvite[]> | null;
  userAvailability?: Relationship<UserAvailability> | null;
  nominations?: Relationship<EstateNomination[]> | null;
  distributionAlternates?: Relationship<DistributionAlternate[]> | null;
  pivot?: Relationship<ProtectionMandataryVerifier> | null;
  referral?: Relationship<Referral> | null;
  notes?: Relationship<Note[]> | null;
  outlookAccount?: Relationship<OutlookAccount> | null;
  hubspotDealId?: string | number;
  // NB this isn't returned in UserResource
  stripeId?: string;
  policyConsentLastGivenAt?: string;

  constructor(model: Partial<User> = {}) {
    super(model);

    this.id = model.id;
    this.i = model.i;
    this.email = model.email;
    this.password = model.password;
    this.emailVerifiedAt = model.emailVerifiedAt;
    this.passwordChangedAt = model.passwordChangedAt;
    this.isSignedUp = model.isSignedUp;
    this.roleId = model.roleId;
    this.active = model.active;
    this.isDemo = model.isDemo;
    this.timezone = model.timezone;
    this.lastLoginAt = model.lastLoginAt;
    this.signedUpAt = model.signedUpAt;
    this.lastLoginIp = model.lastLoginIp;
    this.avatarUrl = model.avatarUrl;
    this.invitationToken = model.invitationToken;
    this.homePage = model.homePage;
    this.primaryContact = this.getPrimaryContact();
    this.receiveUnreadInboxMessagesByMail = model.receiveUnreadInboxMessagesByMail;
    this.hasDarkTheme = model.hasDarkTheme;
    this.locale = model.locale;
    this.otpEnabled = model.otpEnabled;
    this.emailOtpEnabled = model.emailOtpEnabled;
    this.partnershipInvitationId = model.partnershipInvitationId;
    this.hubspotDealId = model.hubspotDealId;
    this.hasPassword = model.hasPassword;
    this.stripeId = model.stripeId;
    this.policyConsentLastGivenAt = model.policyConsentLastGivenAt;

    this.contact = Contact.mapModel(model.contact);
    // @ts-ignore
    this.estateAccount = EstateAccount.mapModelOrNull(model.estateAccount);
    this.professional = Professional.mapModelOrNull(model.professional);
    // @ts-ignore
    this.dependants = EstateAccount.mapModelsOrNull(model.dependants);
    this.mailNotifications = MailNotification.mapModelsOrNull(model.mailNotifications);
    this.company = Company.mapModelOrNull(model.company);
    // @ts-ignore
    this.alternateBeneficiaryToEstateAccounts = EstateAccount.mapModelsOrNull(
      model.alternateBeneficiaryToEstateAccounts
    );
    // @ts-ignore
    this.alternateExecutorToEstateAccounts = EstateAccount.mapModelsOrNull(
      model.alternateExecutorToEstateAccounts
    );
    this.hubspotMeetingLinks = HubspotMeetingLink.mapModelsOrNull(model.hubspotMeetingLinks);
    this.metrics = Metric.mapModelsOrNull(model.metrics);
    this.calendarMeetingLinks = CalendarMeetingLink.mapModelsOrNull(model.calendarMeetingLinks);
    this.timeEntries = TimeEntryCollection.mapModel(model.timeEntries);
    this.trustPilot = TrustPilot.mapModelOrNull(model.trustPilot);
    this.teams = Team.mapModelsOrNull(model.teams);
    this.otpContacts = OtpContactCollection.mapModel(model.otpContacts);
    this.estateTasks = UserEstateTaskCollection.mapModel(model.estateTasks);
    this.billing = Billing.mapModelOrNull(model.billing);
    this.partnershipInvitation = PartnershipInvitation.mapModelOrNull(model.partnershipInvitation);
    this.vaultSignUpToken = EstateSignUpToken.mapModelOrNull(model.vaultSignUpToken);
    this.settlementSignUpToken = EstateSignUpToken.mapModelOrNull(model.settlementSignUpToken);
    this.teamUsers = TeamUser.mapModelsOrNull(model.teamUsers);
    this.teamInvites = TeamInvite.mapModelsOrNull(model.teamInvites);
    this.userAvailability = UserAvailability.mapModelOrNull(model.userAvailability);
    this.nominations = EstateNomination.mapModels(model.nominations);
    this.distributionAlternates = DistributionAlternate.mapModelsOrNull(
      model.distributionAlternates
    );
    this.referral = Referral.mapModelOrNull(model.referral);
    this.pivot =
      model.pivot && Object.keys(model.pivot).includes('frequency')
        ? ProtectionMandataryVerifier.mapModelOrNull(model.pivot)
        : null;
    this.notes = Note.mapModelsOrNull(model.notes);
    this.outlookAccount = OutlookAccount.mapModelOrNull(model.outlookAccount);
  }

  getModelName(): ModelType {
    return ModelType.USER;
  }

  get name(): string {
    return (
      this.estateAccount?.organizationName ??
      this.contact?.primaryContactAlias?.fullName ??
      this.contact?.fullName
    );
  }

  get nameWithEmail(): string {
    return `${this.name} (${this.email})`;
  }

  get avatarLetters(): string {
    if (this.estateAccount?.organizationName) {
      return this.estateAccount.organizationFirstLetter;
    }

    if (this.contact?.firstLetters) {
      return this.contact.firstLetters;
    }

    return this.email?.slice(0, 2) ?? '';
  }

  get displayInitials(): string {
    return this.contact?.initials || this.avatarLetters;
  }

  get roleName(): string | undefined {
    return PredefinedUserRole.getByKey(this.roleId as number, 'id')?.value;
  }

  setEstateRoleId(roleId: number): void {
    if (!this.estateAccount) {
      return;
    }

    if (!this.estateAccount.meta.category) {
      this.estateAccount.meta.category = this.contact?.isAnAdult
        ? BeneficiaryCategory.ADULT.key
        : BeneficiaryCategory.CHILD.key;
    }

    this.estateAccount.setRoleId(roleId);
  }

  get address(): Address | undefined | null {
    return this.isCompanyGuardian ? this.company?.address : this.contact?.address;
  }

  set address(value: Address | undefined | null) {
    if (this.isCompanyGuardian) {
      if (!this.company) {
        return;
      }

      this.company.address = value;
    } else {
      if (!this.contact) {
        return;
      }

      this.contact.address = value;
    }
  }

  get hasUploadedImage(): boolean {
    return !!this.avatarUrl;
  }

  get displayDependantNames(): string | undefined {
    return this.dependants
      ?.map((dependent: EstateAccount): string => dependent.user.name)
      .join(', ');
  }

  get displayGuardianNames(): string | undefined {
    return this.estateAccount?.guardianUsers
      ?.map((guardian: User) => guardian.contact?.fullName)
      .join(', ');
  }

  get isDefault(): boolean {
    return this.roleId === PredefinedUserRole.DEFAULT.id;
  }

  get isAdmin(): boolean {
    return this.roleId === PredefinedUserRole.ADMIN.id;
  }

  get isSuperAdmin(): boolean {
    return this.roleId === PredefinedUserRole.SUPER_ADMIN.id;
  }

  get hasAdminRole(): boolean {
    return this.isAdmin || this.isSuperAdmin;
  }

  get isLegalGuardian(): boolean {
    return !!this.dependants?.length;
  }

  get isPredeceasedChild(): boolean {
    return (
      this.contact?.dateOfDeath !== null &&
      this.estateAccount?.meta.relationshipType === EstateRelationshipType.CHILD.key
    );
  }

  get isOnlyLegalGuardian(): boolean {
    return this.isLegalGuardian && !this.estateAccount;
  }

  getPrimaryContact(): string | undefined {
    return this.contact?.phoneNumber || this.email;
  }

  // TODO: @Thenu remove this function once the contact module goes live
  get displayMemberType(): string | undefined {
    if (this.professional) {
      return this.professional.professionValue;
    }

    if (this.estateAccount) {
      if (this.estateAccount.isEstateProfessional || this.estateAccount.isPlanner) {
        return this.estateAccount.roleName;
      }

      if (this.alternateBeneficiaryToEstateAccounts?.length) {
        return Lang.t('beneficiary.alternate-beneficiary');
      }

      return this.estateAccount.displayRelationshipType;
    }

    if (this.isLegalGuardian) {
      return Lang.t('estateaccount.legal-guardian');
    }

    return Lang.t('user.member');
  }

  get displayContactType(): string | undefined {
    if (this.professional) {
      return this.professional.professionValue;
    }

    if (this.estateAccount) {
      if (this.estateAccount.isPlanner) {
        return '';
      }

      if (this.estateAccount.isEstateProfessional) {
        return this.estateAccount.roleName;
      }

      if (this.estateAccount.isOrganization) {
        return Lang.t('beneficiary.charity');
      }

      return this.estateAccount.displayContactRelationshipType;
    }

    if (this.isLegalGuardian) {
      return Lang.t('estateaccount.legal-guardian');
    }

    return Lang.t('user.member');
  }

  get inviteTooltip(): string {
    if (this.isSignedUp) {
      return Lang.t('user.name-already-platform', {
        name: this.name
      });
    }

    if (this.contact?.isAMinor) {
      return Lang.t('user.minors-estate-professional');
    }

    if (!this.email) {
      return Lang.t(`user.cannot-please-name`, {
        name: this.name
      });
    }

    return Lang.t('user.invite-name-estate', { name: this.name });
  }

  get hasPartnerRelationship(): boolean {
    if (!this.estateAccount) {
      return false;
    }

    return [
      EstateRelationshipType.SPOUSE.key,
      EstateRelationshipType.COMMON_LAW_PARTNER.key
    ].includes(this.estateAccount.meta.relationshipType);
  }

  get isChild(): boolean {
    if (!this.estateAccount) {
      return false;
    }

    return this.estateAccount.meta?.relationshipType === EstateRelationshipType.CHILD.key;
  }

  get isPublicGuardian(): boolean {
    return (
      this.isLegalGuardian &&
      !!this.dependants?.some(
        (dependant: EstateAccount): boolean => dependant.isUnderPublicGuardianship
      )
    );
  }

  get isCompanyGuardian(): boolean {
    return this.isLegalGuardian && !!this.company;
  }

  get dependantsFullNameArray(): string[] | string {
    return (
      this.dependants?.map((dependant: EstateAccount): string => dependant.user.contact.fullName) ??
      ''
    );
  }

  get alternateToFullNameArray(): string[] {
    return [
      ...(this.alternateExecutorToEstateAccounts?.map(
        (alternateTo: EstateAccount): string => alternateTo.user.contact.fullName
      ) ?? ''),
      ...(this.alternateBeneficiaryToEstateAccounts?.map(
        (alternateTo: EstateAccount): string => alternateTo.user.contact.fullName
      ) ?? '')
    ];
  }

  get displayGuardianshipType(): string {
    if (this.isPublicGuardian) {
      return Lang.t('user.public');
    }

    if (this.isCompanyGuardian) {
      return Lang.t('user.private-company');
    }

    return Lang.t('user.private-family');
  }

  createHubSpotMeetingLinks(): void {
    this.hubspotMeetingLinks = HubSpotMeetingLinkType.getValues<HubSpotMeetingLinkType>().map(
      (meetingValue: HubSpotMeetingLinkType) =>
        this.hubspotMeetingLinks?.find(
          (meetingLink: HubspotMeetingLink): boolean => meetingLink.type === meetingValue.key
        ) ??
        HubspotMeetingLink.mapModel({
          type: meetingValue.key
        })
    );
  }

  createCalendarMeetingLinks(permission?: AuthUserPermission): void {
    const linkTypes: CalendarMeetingLinkType[] = [
      CalendarMeetingLinkType.INTAKE,
      CalendarMeetingLinkType.GUIDANCE,
      CalendarMeetingLinkType.WILL_REVIEW,
      ...(permission?.canAccessAnnualReview ? [CalendarMeetingLinkType.ANNUAL_REVIEW] : [])
    ];

    this.calendarMeetingLinks = linkTypes.map(
      (meetingValue: CalendarMeetingLinkType) =>
        this.calendarMeetingLinks?.find(
          (meetingLink: CalendarMeetingLink): boolean => meetingLink.type === meetingValue.key
        ) ??
        CalendarMeetingLink.mapModel({
          userId: this.id,
          type: meetingValue.key
        })
    );
  }

  get isConsultant(): boolean {
    if (this.company) {
      return this.company!.isPartner && !this.company!.isClearEstate;
    }

    return !!this.professional?.company?.isPartner && !this.professional?.company?.isClearEstate;
  }

  get isAdvisor(): boolean {
    if (!this.professional) {
      return false;
    }

    return !this.professional?.company?.isClearEstate && !this.professional?.company?.isPartner;
  }

  get isStaff(): boolean {
    if (!this.professional) {
      return false;
    }

    return !!(this.professional.company?.isClearEstate || this.company?.isClearEstate);
  }

  get isB2BPartner(): boolean {
    return !!this.company?.isB2BPartner;
  }

  get isAccountant(): boolean {
    return !!this.professional?.isAccountant;
  }

  get isStaffAccountant(): boolean {
    return this.isAccountant && this.isStaff;
  }

  get isStaffAdminAccountant(): boolean {
    return this.isStaffAccountant && this.isAdmin;
  }

  get isLawyer(): boolean {
    return !!this.professional?.isLawyer;
  }

  get isNotary(): boolean {
    return !!this.professional?.isNotary;
  }

  get isFuneralDirector(): boolean {
    return !!this.professional?.isFuneralDirector;
  }

  get isAdminProfessional(): boolean {
    return !!this.professional && (this.isAdmin || this.professional.isBankAdmin);
  }

  get isEstateManager(): boolean {
    return !!this.professional?.isEstateManager;
  }

  get isEstateProfessional(): boolean {
    return !!this.professional?.isEstateProfessional;
  }

  get canBeSetAsPrimary(): boolean {
    return !!this.professional?.canBeSetAsPrimary;
  }

  get isJuniorEstateProfessional(): boolean {
    return !!this.professional?.isJuniorEstateProfessional;
  }

  get isFinancialAdvisor(): boolean {
    return !!this.professional?.isFinancialAdvisor;
  }

  get isAssociateFinancialAdvisor(): boolean {
    return !!this.professional?.isAssociateFinancialAdvisor;
  }

  get isFinancialAdvisorAssistant(): boolean {
    return !!this.professional?.isFinancialAdvisorAssistant;
  }

  get isInFinancialAdvisorProfessionGroup(): boolean {
    return (
      this.isFinancialAdvisor ||
      this.isAssociateFinancialAdvisor ||
      this.isFinancialAdvisorAssistant
    );
  }

  get isTrustOfficer(): boolean {
    return !!this.professional?.isTrustOfficer;
  }

  get isTrustManager(): boolean {
    return !!this.professional?.isTrustManager;
  }

  get professionId(): number | undefined {
    return this.professional?.professionId;
  }

  get isAdminConsultant(): boolean {
    return this.isConsultant && this.isAdminProfessional;
  }

  get hasMailNotifications(): boolean {
    if (!this.mailNotifications?.length) {
      return false;
    }

    return this.mailNotifications.length > 0;
  }

  get hasNoticeToAdministerEstateMailNotifications(): boolean {
    return (
      this.hasMailNotifications &&
      !!this.mailNotifications?.some(
        (mailNotification: MailNotification): boolean =>
          mailNotification.esignEnvelope?.type === EsignEnvelopeType.NOTIFY_BENEFICIARIES.key
      )
    );
  }

  get isPrimaryExecutor(): boolean {
    return !!this.estateAccount?.isExecutor && this.alternateExecutorToEstateAccounts?.length === 0;
  }

  get isLocaleEnglish(): boolean {
    return this.locale === Locale.ENGLISH.key;
  }

  get isLocaleFrench(): boolean {
    return this.locale === Locale.FRENCH.key;
  }

  get hasVerifiedEmail(): boolean {
    return !!this.emailVerifiedAt;
  }

  get hasOnlySpecificBeneficiaries(): boolean {
    return (
      // @ts-ignore
      this.dependants?.filter((dependant: EstateAccount) => dependant.isSpecificBeneficiary)
        .length === this.dependants?.length
    );
  }

  get hasTrustPilot(): boolean {
    return !!this.trustPilot;
  }

  get isDeceased(): boolean {
    return !!this.contact?.dateOfDeath || !!this.contact?.isDeceased;
  }

  get teamThatUserLeads(): Team | undefined {
    return this.teams?.find((team: Team): boolean => team.leadUserId === this.id);
  }

  get isTeamLead(): boolean {
    return !!this.teamThatUserLeads;
  }

  get juniorEstateProfessionalTeam(): Team | undefined {
    return this.teams?.find(
      (team: Team): boolean => team.type === TeamType.JUNIOR_ESTATE_PROFESSIONAL.key
    );
  }

  get isOutOfOffice(): boolean {
    if (!this.userAvailability) {
      return false;
    }

    return !!this.userAvailability.isUnavailable && !this.estateAccount?.deletedAt;
  }

  get canBeImpersonated(): boolean {
    return (
      this.isEstateProfessional ||
      this.isJuniorEstateProfessional ||
      this.isAccountant ||
      this.isFinancialAdvisor
    );
  }

  get areContactDetailsComplete(): boolean {
    return (
      !!this.email &&
      !!this.contact?.isCompleted &&
      !!this.contact?.phoneNumber &&
      !!this.contact?.address?.isComplete
    );
  }

  get areCommunicationDetailsComplete(): boolean {
    return !!this.email && !!this.contact?.phoneNumber;
  }

  get daysSinceSignup(): number | null {
    if (!this.signedUpAt) {
      return null;
    }

    return numberOfDaysFromTodayToDate(this.signedUpAt);
  }

  get daysSinceLastLogin(): number | null {
    if (!this.lastLoginAt) {
      return null;
    }

    return numberOfDaysFromTodayToDate(this.lastLoginAt);
  }

  get displayAddressType(): string {
    return this.isCompanyGuardian
      ? Lang.t('member.company-address')
      : Lang.t('member.office-address');
  }

  get daysSinceAccountCreation(): number {
    const dayInMilliSeconds: number = 1000 * 60 * 60 * 24;
    const timeDifference: number = new Date().getTime() - new Date(this.createdAt).getTime();

    return Math.round(timeDifference / dayInMilliSeconds);
  }

  setAsChildBeneficiary(): void {
    if (!this.estateAccount) {
      return;
    }

    if (this.estateAccount.isExecutor) {
      this.estateAccount.roleId = PredefinedEstateRole.EXECUTOR_AND_BENEFICIARY.id;
    } else {
      this.estateAccount.roleId = PredefinedEstateRole.BENEFICIARY.id;
    }

    this.estateAccount.meta = {
      ...this.estateAccount.meta,
      category: BeneficiaryCategory.CHILD.key,
      relationshipType: EstateRelationshipType.CHILD.key
    };
  }

  setAsPredeceasedChildBeneficiary(): void {
    if (!this.estateAccount) {
      return;
    }

    this.contact.isDeceased = true;
    this.estateAccount.roleId = PredefinedEstateRole.BENEFICIARY.id;
    this.estateAccount.meta = {
      ...this.estateAccount.meta,
      category: BeneficiaryCategory.CHILD.key,
      relationshipType: EstateRelationshipType.CHILD.key,
      isNotServed: true,
      unservedReason: UnservedReason.DEATH.key
    };
  }
}
